/**
 * OrderFiltersBar Component
 * Filter controls for customer orders (desktop view)
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-007
 */

import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Search, Filter, X, Calendar, SlidersHorizontal } from 'lucide-react';
import type { CustomerOrderFilters } from '../types/order.types';
import type { OrderStatus } from '@/types/order';

// ============================================
// STATUS OPTIONS
// ============================================

const STATUS_OPTIONS: { value: OrderStatus; label: string }[] = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'PROCESSING', label: 'Processing' },
  { value: 'READY', label: 'Ready' },
  { value: 'DISPATCHED', label: 'Dispatched' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

// ============================================
// COMPONENT PROPS
// ============================================

export interface OrderFiltersBarProps {
  filters: CustomerOrderFilters;
  onSearchChange: (searchTerm: string) => void;
  onStatusToggle: (status: OrderStatus) => void;
  onClearFilters: () => void;
  onOpenMobileFilters?: () => void;
  activeFilterCount: number;
}

// ============================================
// COMPONENT
// ============================================

/**
 * OrderFiltersBar Component
 * Desktop filter controls with search and status filters
 *
 * @example
 * ```tsx
 * <OrderFiltersBar
 *   filters={filters}
 *   onSearchChange={setSearchTerm}
 *   onStatusToggle={toggleStatus}
 *   onClearFilters={clearFilters}
 *   activeFilterCount={2}
 * />
 * ```
 */
export function OrderFiltersBar({
  filters,
  onSearchChange,
  onStatusToggle,
  onClearFilters,
  onOpenMobileFilters,
  activeFilterCount,
}: OrderFiltersBarProps) {
  const [searchInput, setSearchInput] = useState(filters.searchTerm || '');

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    onSearchChange(value);
  };

  const selectedStatuses = filters.statuses || [];

  return (
    <div className="space-y-4">
      {/* Desktop View */}
      <div className="hidden md:flex items-center gap-3">
        {/* Search Input */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by order number..."
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Status Filter Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="default">
              <Filter className="h-4 w-4 mr-2" />
              Status
              {selectedStatuses.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {selectedStatuses.length}
                </Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {STATUS_OPTIONS.map((option) => (
              <DropdownMenuCheckboxItem
                key={option.value}
                checked={selectedStatuses.includes(option.value)}
                onCheckedChange={() => onStatusToggle(option.value)}
              >
                {option.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Clear Filters Button */}
        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            size="default"
            onClick={onClearFilters}
            className="text-slate-600 hover:text-slate-900"
          >
            <X className="h-4 w-4 mr-2" />
            Clear ({activeFilterCount})
          </Button>
        )}
      </div>

      {/* Mobile View */}
      <div className="md:hidden space-y-3">
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search orders..."
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Mobile Filter Button */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="default"
            onClick={onOpenMobileFilters}
            className="flex-1"
          >
            <SlidersHorizontal className="h-4 w-4 mr-2" />
            Filters
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-2">
                {activeFilterCount}
              </Badge>
            )}
          </Button>

          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onClearFilters}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Active Filter Tags */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-slate-600">Active filters:</span>
          {selectedStatuses.map((status) => (
            <Badge
              key={status}
              variant="secondary"
              className="cursor-pointer hover:bg-slate-200"
              onClick={() => onStatusToggle(status)}
            >
              {STATUS_OPTIONS.find(opt => opt.value === status)?.label}
              <X className="h-3 w-3 ml-1" />
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

export default OrderFiltersBar;
